<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-3">

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
        v-model="search"
        type="text"
        placeholder="Search"
        class="col-span-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        @keyup.enter="fetch" />
    </div>

    <div class="bg-white shadow overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-100 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Practitioner No.</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Profession</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Registration Date</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
          <template v-if="!loading && practitioners.length > 0">
            <tr v-for="(practitioner, idx) in practitioners" :key="idx"
                class="border-b border-gray-100 hover:bg-gray-50 transition">
              <td class="py-3 px-4 font-bold text-primary cursor-pointer hover:underline"
                  @click="viewFile(practitioner.uniquePractitionerNumber)">
                {{ practitioner.uniquePractitionerNumber }}
              </td>
              <td class="py-3 px-4 font-bold">{{ practitioner.firstName }} {{ practitioner.lastName }}</td>
              <td class="py-3 px-4">{{ practitioner.phoneNumber }}</td>
              <td class="py-3 px-4">
                <span v-if="practitioner.form1s[0]">{{ practitioner.form1s[0].professions[0].professionName }}</span>
                <span v-else class="text-red-500">None</span>
              </td>
              <td class="py-3 px-4">{{ dateFormat(practitioner.registrationDate) }}</td>
              <td class="py-3 px-4">
                  <span v-if="practitioner.active"
                        class="text-xs font-medium px-3 py-1 rounded-full inline-block text-green-700 font-bold bg-green-100">
                    Active
                  </span>
                <span v-else class="text-xs font-medium px-3 py-1 rounded-full inline-block text-red-700 bg-red-100">
                    Inactive
                  </span>
              </td>
              <td class="py-3 px-4">
                <button @click="viewFile(practitioner.uniquePractitionerNumber)" class="btn-sm-primary"
                        title="View File">
                  <i class="fa-solid fa-eye"></i> View File
                </button>
              </td>
            </tr>
          </template>
          <tr v-if="loading">
            <td colspan="7" class="py-16 text-center">
              <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
              </td>
            </tr>
          <template v-if="!loading && practitioners.length === 0">
            <tr>
              <td colspan="7" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <i class="fa fa-folder-open text-6xl mb-4"></i>
                  <p class="text-lg font-medium text-gray-500">No Practitioners Found</p>
                  <p class="text-sm text-gray-400 mt-2">No insolvency practitioners in the registry.</p>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <Pagination :pagination="pagination" :page="currentPage" :limit="limit" label="practitioners" @update="update" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "@/composables";
import { usePractitionersRegistryStore } from "@/stores/practitioners-registry";
import { dateFormat } from "@/commons/composables/utilities";
import Pagination from "@/commons/components/Pagination.vue";
import type { Pagination as PaginationType } from "@/app/types";

interface Profession {
  professionName: string;
}

interface Form1 {
  professions: Profession[];
}

interface Practitioner {
  uniquePractitionerNumber: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  form1s: Form1[];
  registrationDate: string;
  active: boolean;
}

const store = usePractitionersRegistryStore();
const router = useRouter()
const { errorToast } = useToast();

const loading = ref(false)
const currentPage = ref(1)
const limit = ref(10)
const practitioners = ref<Practitioner[]>([])
const pagination = ref<PaginationType | undefined>(undefined);
const search = ref("")

onMounted(() => {
  fetch();
})

const fetch = () => {
  loading.value = true;
  store.fetchPractitionerRegistry({ search: search.value, page: currentPage.value, limit: limit.value });
}

const viewFile = (id: string) => {
  router.push("/home/non-individuals/practitioner-viewer/" + id);
  router.go(0);
}

function update(pg: number) {
  currentPage.value = pg;
  fetch();
}

watch(search, () => {
  fetch();
})

watch(() => store.practitioner_registry, (data) => {
  if (data?.data) {
    loading.value = false;
    practitioners.value = data.data.content || [];
    pagination.value = {
      currentPage: currentPage.value,
      nextPage: currentPage.value + 1,
      totalRows: data.totalElements || 0,
      totalPages: data.totalPages || 0
    };
  }
})

watch(() => store.error, (data) => {
  if (data) {
    loading.value = false;
    errorToast("Error", JSON.stringify(data.message));
  }
})
</script>
